/**
 * IntegratrAdapter.ts - IntegratrAdapter
 * 
 * CHANGE HISTORY:
 * - 251226_0857 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { LogLevel, LogEntry } from '../models/LogEntry.js';
import { type Actr } from '@opcentral/typr-lib';

/**
 * IntegratrAdapter class
 */
interface IntegratrOptions {
  minLevel?: number;
  endpoint?: string;
  batchSize?: number;
  flushInterval?: number;
  [key: string]: any;
}

interface Integratr {
  hasIntegration(serviceName: string): boolean;
  send(serviceName: string, eventType: string, data: any): Promise<void>;
}

export class IntegratrAdapter {
  options: IntegratrOptions;
  integratr: Integratr | null;
  minLevel: number;
  services: string[];

  constructor(options: IntegratrOptions = {}) {
    this.minLevel = options.minLevel ?? LogLevel.INFO;
    this.services = options.services ?? [];
    this.options = {
      minLevel: this.minLevel,
      integratr: options.integratr || null, // Integratr instance
      services: this.services, // Array of service names to send to
      ...options
    };
    
    this.integratr = options.integratr || null;
  }
  
  /**
   * Set Integratr instance
   */
  setIntegratr(integratr: Integratr): void {
    this.integratr = integratr;
  }
  
  /**
   * Write log entry
   */
  async write(logEntry: LogEntry): Promise<void> {
    if (!logEntry.isLevelEnabled(this.minLevel as any)) {
      return;
    }
    
    if (!this.integratr) {
      console.warn('Integratr not configured for logging');
      return;
    }
    
    const logData = logEntry.toStructuredLog();
    
    // Send to each configured service
    for (const serviceName of this.services) {
      try {
        await this.sendToService(serviceName, logEntry, logData);
      } catch (error) {
        console.error(`Failed to send log to ${serviceName}:`, error);
      }
    }
  }
  
  /**
   * Send to specific service
   */
  async sendToService(serviceName: string, logEntry: LogEntry, logData: any): Promise<void> {
    switch (serviceName.toLowerCase()) {
      case 'datadog':
        await this.sendToDatadog(logEntry, logData);
        break;
      
      case 'sentry':
        await this.sendToSentry(logEntry, logData);
        break;
      
      case 'newrelic':
      case 'new-relic':
        await this.sendToNewRelic(logEntry, logData);
        break;
      
      case 'logrocket':
        await this.sendToLogRocket(logEntry, logData);
        break;
      
      case 'splunk':
        await this.sendToSplunk(logEntry, logData);
        break;
      
      default:
        // Generic integration
        await this.sendToGeneric(serviceName, logEntry, logData);
    }
  }
  
  /**
   * Send to Datadog
   */
  async sendToDatadog(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('datadog')) {
      await this.integratr.send('datadog', 'log', logData);
    }
  }
  
  /**
   * Send to Sentry
   */
  async sendToSentry(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('sentry')) {
      // Send error to Sentry
      if (logEntry.level >= LogLevel.ERROR && logEntry.error) {
        await this.integratr.send('sentry', 'error', {
          error: logEntry.error,
          message: logEntry.message,
          level: logEntry.levelName.toLowerCase(),
          tags: logEntry.tags,
          extra: logData
        });
      }
    }
  }
  
  /**
   * Send to New Relic
   */
  async sendToNewRelic(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('newrelic')) {
      await this.integratr.send('newrelic', 'log', {
        message: logEntry.message,
        level: logEntry.levelName,
        timestamp: logEntry.timestamp,
        attributes: logData
      });
    }
  }
  
  /**
   * Send to LogRocket
   */
  async sendToLogRocket(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('logrocket')) {
      await this.integratr.send('logrocket', 'log', {
        level: logEntry.levelName.toLowerCase(),
        message: logEntry.message,
        data: logData
      });
    }
  }
  
  /**
   * Send to CloudWatch
   */
  async sendToCloudWatch(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('cloudwatch')) {
      await this.integratr.send('cloudwatch', 'log', {
        message: logEntry.message,
        timestamp: logEntry.timestamp,
        data: logData
      });
    }
  }
  
  /**
   * Send to Splunk
   */
  async sendToSplunk(logEntry: LogEntry, logData: any): Promise<void> {
    if (this.integratr && this.integratr.hasIntegration('splunk')) {
      await this.integratr.send('splunk', 'event', {
        event: logData,
        time: new Date(logEntry.timestamp).getTime() / 1000,
        source: 'opcentral',
        sourcetype: '_json'
      });
    }
  }
  
  /**
   * Send to generic integration
   */
  async sendToGeneric(serviceName: any, logEntry: any, logData: unknown): Promise<void> {
    if ((this.integratr as any).hasIntegration(serviceName)) {
      await (this.integratr as any).send(serviceName, 'log', logData);
    }
  }
}

export default IntegratrAdapter;
